import React, { FC, useCallback } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import { DaDataSuggestion, DaDataParty } from 'react-dadata';

import { Button, Desktop, GridColumn, GridRow, Label, linkAnchorClass, useMobile } from 'Modules/ui2';
import { validators, getError } from 'Modules/core/forms';

import { DefaultComponentProps } from '../types';
import { CheckBoxField, InputField, ParticipantSelectField } from '../../Fields';
import { PersonalDataError } from '../PersonalDataError';
import { Documents } from '../Documents';
import { useRequest } from '../hooks';
import RegistrationIcon from '../Icons/registration.sprite.svg';
import styles from './index.module.scss';


const phoneMask = '+7(999)999-99-99';
const phoneValidation = getError<string>(
    { key: 'phone' },
    v => v && v.length === phoneMask.length,
);

export type LeadValues = {
    surname: string;
    firstName: string;
    company: DaDataSuggestion<DaDataParty>;
    phone: string;
    email: string;
    email2: string;
    usePersonalData: boolean;
}

export type LeadResult = {
    Uid: string;
    INN: string;
    Email: string;
    Surname: string;
    FirstName: string;
    MiddleName: string;
    PhoneNumber: string;
    LeadgeneratorName: string;
    TariffTypeCode: string;
    IsMobile: boolean;
}

const mapFormValuesToApi = (data: LeadValues) => ({
    INN: data.company.data.inn,
    Email: data.email,
    PhoneNumber: data.phone,
    Surname: data.surname,
    FirstName: data.firstName,
});

export const Lead: FC<DefaultComponentProps> = ({ t, setStage }) => {
    const onSuccess = useCallback(
        (values: LeadValues, leadResult: LeadResult) => setStage('rate', { values, leadResult }),
        [setStage],
    );
    const { busy, request } = useRequest<LeadValues>('/leads', mapFormValuesToApi, 'POST', onSuccess);
    const isMobile = useMobile();
    return (
        <div>
            <Label
                className={styles.header}
                weight='demibold'
                textSize={isMobile ? '24' : '32'}
                component='span'
            >
                {t('lead.header')}
            </Label>
            <GridRow>
                <GridColumn size={6}>
                    <Form<LeadValues>
                        subscription={{ hasValidationErrors: true, values: true }}
                        onSubmit={request}
                    >
                        {({ hasValidationErrors, values }) => (
                            <>
                                <form className={styles.form}>
                                    <Field
                                        name='surname'
                                        component={InputField}
                                        size='56'
                                        validate={validators.required}
                                        className={styles.field}
                                        label={t('lead.fields.surname.label')}
                                        placeholder={t('lead.fields.surname.placeholder')}
                                    />
                                    <Field
                                        name='firstName'
                                        component={InputField}
                                        size='56'
                                        validate={validators.required}
                                        className={styles.field}
                                        label={t('lead.fields.firstName.label')}
                                        placeholder={t('lead.fields.firstName.placeholder')}
                                    />
                                    <Field
                                        name='company'
                                        component={ParticipantSelectField}
                                        size='56'
                                        validate={validators.required}
                                        className={styles.field}
                                        label={t('lead.fields.company.label')}
                                    />
                                    <Field
                                        name='phone'
                                        component={InputField}
                                        size='56'
                                        validate={validators.pipe(
                                            validators.required,
                                            phoneValidation,
                                        )}
                                        className={styles.field}
                                        label={t('lead.fields.phone.label')}
                                        placeholder={t('lead.fields.phone.placeholder')}

                                        mask={phoneMask}
                                    />
                                    <Field
                                        name='email'
                                        component={InputField}
                                        size='56'
                                        validate={validators.pipe(
                                            validators.required,
                                            validators.is.email,
                                        )}
                                        className={styles.field}
                                        label={t('lead.fields.email.label')}
                                        placeholder={t('lead.fields.email.placeholder')}
                                    />
                                    <Field
                                        name='email2'
                                        component={InputField}
                                        className={styles.hidden}
                                    />
                                    <div>
                                        <Field
                                            name='usePersonalData'
                                            component={CheckBoxField}
                                            validate={validators.required}
                                            type='checkbox'
                                        >
                                            <Label
                                                component='span'
                                                textSize='14'
                                            >
                                                {t('lead.fields.legal', { linkClass: linkAnchorClass })}
                                            </Label>
                                        </Field>
                                        <FormSpy
                                            subscription={{ errors: true }}
                                            component={PersonalDataError}
                                        />
                                    </div>
                                </form>
                                <Button
                                    variant='normal'
                                    size='56'
                                    className={styles.button}
                                    disabled={hasValidationErrors || busy}
                                    onClick={() => request(values)}
                                    loading={busy}
                                >
                                    {t('lead.button')}
                                </Button>
                            </>
                        )}
                    </Form>
                </GridColumn>
                <Desktop>
                    <GridColumn size={6}>
                        <div className={styles.imgContainer}>
                            <RegistrationIcon />
                        </div>
                    </GridColumn>
                </Desktop>
            </GridRow>
            <GridRow>
                <GridColumn size={12}>
                    <Documents className={styles.documents} />
                </GridColumn>
            </GridRow>
        </div>
    );
};
